import { generateS3ItemPreSignedUrlV1 } from "@/lib/data/requests/s3";
import { updateFirmFileV1 } from "@/lib/data/requests/firm/firm-file";
import { updateMatterFileV1 } from "@/lib/data/requests/matters/matter-file";
import type { FirmFile, MatterFile } from "@/lib/types";

import { maskSignerTemplateMarkersInPdf } from "./signatureRequestTemplateFields";

type GeneratedFileType<T extends MatterFile | FirmFile> = T extends MatterFile
  ? "matter-file"
  : "firm-file";

export async function maskGeneratedSignatureRequestPdf<
  T extends MatterFile | FirmFile,
>(file: T, fileType: GeneratedFileType<T>): Promise<T> {
  if (!file.presignedUrlKey) return file;

  const downloadUrl = await generateS3ItemPreSignedUrlV1(fileType)({
    clientMethod: "get_object",
    key: file.presignedUrlKey,
  });
  const downloadResponse = await fetch(downloadUrl.data.presignedUrl);
  if (!downloadResponse.ok) {
    throw new Error("Unable to download the generated signature document.");
  }

  const maskedPdf = await maskSignerTemplateMarkersInPdf(
    await downloadResponse.arrayBuffer(),
  );
  if (!maskedPdf) return file;

  const uploadUrl = await generateS3ItemPreSignedUrlV1(fileType)({
    clientMethod: "put_object",
    fileName: file.name,
    key: file.presignedUrlKey,
  });
  const uploadResponse = await fetch(uploadUrl.data.presignedUrl, {
    method: "PUT",
    headers: { "Content-Type": "application/pdf" },
    body: maskedPdf,
  });
  if (!uploadResponse.ok) {
    throw new Error("Unable to save the generated signature document.");
  }

  if (fileType === "firm-file") {
    return (
      await updateFirmFileV1({
        id: file.id,
        dateModified: file.dateModified,
        presignedUrlKey: uploadUrl.data.key,
      })
    ).data as T;
  }

  const matterFile = file as MatterFile;
  return (
    await updateMatterFileV1({
      id: matterFile.id,
      dateModified: matterFile.dateModified,
      matterId: matterFile.matterId,
      presignedUrlKey: uploadUrl.data.key,
    })
  ).data as T;
}
